import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK, STATUS } from './agencyChartTheme';
import { IOutcomeMetric, OutcomeTone } from './agencyOutcomes';
import { HistoryWaitNote } from './HistoryWaitNote';

const useStyles = createUseStyles({
    report: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14
    },

    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 14
    },

    card: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,
        width: '100%',
        padding: '15px 16px 16px',
        borderRadius: 12,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',
        transition: 'border-color 140ms ease, background 140ms ease',

        '&:hover, &:focus-visible': {
            borderColor: BRAND.deepTeal,
            background: BRAND.tealWash
        }
    },

    head: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },

    title: {
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: BRAND.deepTeal
    },

    value: {
        fontSize: 34,
        fontWeight: 600,
        lineHeight: 1.05,
        fontVariantNumeric: 'tabular-nums'
    },

    caption: {
        fontSize: 12.5,
        lineHeight: 1.4,
        color: CHART_INK.label
    },

    // In the figure's place when there is nobody to count, so the card says why rather than showing a
    // zero that reads as a finding.
    noValue: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.35,
        color: CHART_INK.label
    },

    // Bea's read on the figure. Set apart from the caption because one is what the number counts and
    // the other is somebody's interpretation of it, and a reader is owed the difference.
    narration: {
        display: 'flex',
        gap: 8,
        marginTop: 8,
        paddingTop: 10,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        fontSize: 12,
        lineHeight: 1.5,
        color: CHART_INK.label
    },

    beaMark: {
        width: 20,
        height: 20,
        flexShrink: 0,
        borderRadius: '50%',
        objectFit: 'cover' as const
    },

    more: {
        marginTop: 2,
        fontSize: 11.5,
        fontWeight: 500,
        color: BRAND.deepTeal
    }
});

const VALUE_COLOR: { [key in OutcomeTone]: string } = {
    good: STATUS.settled,
    watch: STATUS.declining
};

/**
 * The outcome figures, at the top of the tab because they are the reason anyone opens it.
 *
 * Each is a button rather than a tile with a link in it: the number is a count of people, and the
 * whole card is the way to the names behind it and to how the figure is counted. Activity charts sit
 * below these deliberately, since they answer whether the tool is being used, which is a different
 * and much smaller question.
 */
export const OutcomesReport: FC<{
    metrics: IOutcomeMetric[];
    /** What the figures still waiting on the caseload's history are waiting for, under the ones shown. */
    pending?: string;
    onOpen: (metric: IOutcomeMetric) => void;
}> = ({ metrics, pending, onOpen }) => {
    const classes = useStyles();

    return (
        <div className={classes.report}>
            {metrics.length > 0 && (
                <div className={classes.grid} data-id="outcomes-report">
                    {metrics.map(metric => (
                        <button
                            key={metric.id}
                            type="button"
                            className={classes.card}
                            data-id="outcome-metric"
                            data-metric={metric.id}
                            onClick={() => onOpen(metric)}
                        >
                            <span className={classes.head}>
                                <span className={classes.title}>{metric.title}</span>
                            </span>

                            {metric.value === undefined
                                ? <span className={classes.noValue}>{metric.caption}</span>
                                : (
                                    <>
                                        <span className={classes.value} style={{ color: VALUE_COLOR[metric.tone] }}>{metric.value}</span>
                                        <span className={classes.caption}>{metric.caption}</span>
                                    </>
                                )}

                            <span className={classes.narration}>
                                <img src="/assets/images/help/helper-bea.png" alt="" className={classes.beaMark} />
                                <span>{metric.narration}</span>
                            </span>

                            <span className={classes.more}>See who &rsaquo;</span>
                        </button>
                    ))}
                </div>
            )}

            {pending && <HistoryWaitNote panel="outcomes">{pending}</HistoryWaitNote>}
        </div>
    );
};
